"use client";
import React, { useEffect, useState } from "react";
import { Button } from "../shadcn/button";
import { Input } from "../shadcn/input";
import { Label } from "../shadcn/label";
import { cn } from "@/utils/helpers";
import { toast } from "sonner";
import { Spin } from "../shadcn/spin";

interface SearchBarProps {
  onInputChange: (value: string) => void;
  underConstructionAlert?: string;
  exampleTexts?: string;
  defaultDimensions?: boolean;
  placeholder?: string;
  defaultValue?: string;
  label?: string;
  extraClasses?: string;
}

const SearchBar: React.FC<SearchBarProps> = ({
  onInputChange,
  underConstructionAlert = "",
  exampleTexts = "",
  defaultDimensions = true,
  placeholder = "",
  defaultValue = "",
  label = "",
  extraClasses = "",
}) => {
  const [searchValue, setSearchValue] = useState(defaultValue || "");
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(event.target.value);
  };

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setIsLoading(true);
    setTimeout(() => {
      setIsLoading(false);
    }, 1500);
    if (searchValue.trim() === "") {
      toast.error("Please enter a search term.");
      return;
    }
    if (searchValue.length < 3) {
      toast.error("Please enter at least 3 characters.");
      return;
    }
    if (searchValue.length > 50) {
      toast.error("Please enter no more than 50 characters.");
      return;
    }
    event.preventDefault();
    onInputChange(searchValue);
  };

  useEffect(() => {
    setSearchValue(defaultValue);
  }, [defaultValue]);

  return (
    <div className={cn("relative w-full", { "md:w-[70vw] lg:w-[50vw]": defaultDimensions })}>
      {label && (
        <Label
          htmlFor="search_directory_input"
          className="text-sm font-medium flex justify-center text-gray-700 dark:text-white mb-5"
        >
          {label}
        </Label>
      )}

      <div className="relative">
        <Input
          id="search_directory_input"
          type="text"
          placeholder={placeholder}
          value={searchValue}
          onChange={handleChange}
          className={cn(
            "pr-20 h-[50px] transition-all duration-200 border border-gray-200 dark:border-secondary focus:border-primary focus:ring-2 focus:ring-primary/30 hover:shadow-lg hover:border-primary",
            extraClasses
          )} // Add padding for button and hover/focus effects
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              handleClick({ preventDefault: () => {} } as React.MouseEvent<HTMLButtonElement>);
            }
          }}
        />
        <Button
          onClick={handleClick}
          className="absolute right-2 top-1/2 -translate-y-1/2 h-[35px] px-4 text-xs"
        >
          {isLoading ? (
            <Spin variant={"primary"} size={"sm"} />
          ) : (
            <span className="flex items-center justify-center">Search</span>
          )}
        </Button>
      </div>

      {underConstructionAlert && (
        <div className="hidden group-hover:block absolute left-1/2 transform -translate-x-1/2 -top-12 bg-gray-800 text-white text-sm rounded py-2 px-4 shadow-lg">
          {underConstructionAlert}
          <div className="absolute left-1/2 transform -translate-x-1/2 bottom-[-6px] w-0 h-0 border-l-8 border-r-8 border-b-8 border-transparent border-t-gray-800"></div>
        </div>
      )}

      {exampleTexts && (
        <div className="mt-2 text-dark text-xs font-normal leading-[18px]">{exampleTexts}</div>
      )}
    </div>
  );
};

export default SearchBar;
